// Pure gesture logic for the ScrollOverHero pull-to-zoom, kept free of the DOM
// and framer-motion so it can be unit-tested in isolation. The component feeds
// it touch coordinates and applies the returned `pull` / `preventDefault`.

export const AXIS_LOCK_PX = 8; // movement before a gesture commits to an axis
export const PULL_RESIST = 0.5; // finger px → stretch px (lower = stiffer)
export const MAX_PULL = 72; // cap on stretch; stays below the drawer overlap

export type GestureAxis = "vertical" | "horizontal" | null;

export interface PullGesture {
  anchorX: number;
  anchorY: number;
  /** True once we have a live anchor (touch started, or arrived, at the top). */
  anchored: boolean;
  /** Locked direction, or null until movement passes AXIS_LOCK_PX. */
  axis: GestureAxis;
  /** Whether a downward pull is currently engaged. */
  pulling: boolean;
}

export interface PullResult {
  gesture: PullGesture;
  /** Stretch distance in px (0 = at rest). */
  pull: number;
  /** Whether the touchmove's default (native scroll) should be prevented. */
  preventDefault: boolean;
}

export function idleGesture(): PullGesture {
  return { anchorX: 0, anchorY: 0, anchored: false, axis: null, pulling: false };
}

export function beginGesture(
  atTop: boolean,
  x: number,
  y: number,
): PullGesture {
  return { anchorX: x, anchorY: y, anchored: atTop, axis: null, pulling: false };
}

export function moveGesture(
  g: PullGesture,
  atTop: boolean,
  x: number,
  y: number,
): PullResult {
  // Pull only exists at the very top; anywhere else, drop everything so the
  // page scrolls normally.
  if (!atTop) {
    return { gesture: idleGesture(), pull: 0, preventDefault: false };
  }

  // Arrived at the top mid-gesture (or first move): anchor here.
  let anchorX = g.anchorX;
  let anchorY = g.anchorY;
  let axis = g.axis;
  if (!g.anchored) {
    anchorX = x;
    anchorY = y;
    axis = null;
  }

  const dx = x - anchorX;
  const dy = y - anchorY;

  // Wait until the gesture clearly favours one axis, so a horizontal photo
  // swipe is never read as a downward pull.
  if (axis === null) {
    if (Math.abs(dx) + Math.abs(dy) <= AXIS_LOCK_PX) {
      return {
        gesture: { anchorX, anchorY, anchored: true, axis: null, pulling: false },
        pull: 0,
        preventDefault: false,
      };
    }
    axis = Math.abs(dy) > Math.abs(dx) ? "vertical" : "horizontal";
  }

  // Horizontal = carousel swipe; never pull, never block native handling.
  if (axis !== "vertical") {
    return {
      gesture: { anchorX, anchorY, anchored: true, axis, pulling: false },
      pull: 0,
      preventDefault: false,
    };
  }

  // Vertical pull-down: stretch (capped) and own the gesture.
  if (dy > 0) {
    return {
      gesture: { anchorX, anchorY, anchored: true, axis, pulling: true },
      pull: Math.min(MAX_PULL, dy * PULL_RESIST),
      preventDefault: true,
    };
  }

  // Vertical but upward/flat: release any pull and re-anchor so a later
  // down-pull starts clean.
  return {
    gesture: { anchorX, anchorY: y, anchored: true, axis, pulling: false },
    pull: 0,
    preventDefault: false,
  };
}
